import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Youtube, Music2, Calendar } from "lucide-react";
import { http } from "@/lib/api";
import Seo from "@/components/Seo";
import { VideoEmbed } from "@/components/VideoEmbed";

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch {
        return "";
    }
}

/**
 * /transmissions/{slug} — page vidéo individuelle.
 *
 * SEO-first : chaque vidéo est une VideoObject schema.org complète avec
 * embedUrl, thumbnailUrl, uploadDate, duration, description. La transcription
 * complète est affichée sous l'embed pour maximiser le contenu indexable
 * (Google + LLMs adorent les pages vidéo avec transcript).
 */
export default function TransmissionDetail() {
    const { slug } = useParams();
    const nav = useNavigate();
    const [video, setVideo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [related, setRelated] = useState([]);

    useEffect(() => {
        setLoading(true);
        http.get(`/videos/${slug}`)
            .then(({ data }) => setVideo(data))
            .catch(() => setVideo(null))
            .finally(() => setLoading(false));
        http.get("/videos", { params: { limit: 4 } })
            .then(({ data }) => setRelated((data || []).filter((v) => v.slug !== slug).slice(0, 3)))
            .catch(() => setRelated([]));
    }, [slug]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Chargement…</div>;
    }
    if (!video) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4">
                <p className="text-muted-foreground">Vidéo introuvable</p>
                <button onClick={() => nav("/transmissions")} className="text-primary underline">
                    Retour à toutes les transmissions
                </button>
            </div>
        );
    }

    // Strip the admin override marker for display
    const cleanDescription = (video.description || "").replace(/^<!-- fol:override -->\n?/, "");

    const videoSchema = {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: video.title,
        description: cleanDescription.slice(0, 500),
        thumbnailUrl: video.thumbnail_url,
        uploadDate: video.published_at,
        duration: video.duration_iso,
        contentUrl: video.external_url,
        embedUrl: video.source === "youtube" ? `https://www.youtube.com/embed/${video.external_id}` : undefined,
        publisher: {
            "@type": "Organization",
            name: "Foundation of Light",
            logo: {
                "@type": "ImageObject",
                url: "https://foundation-of-light.com/brand/logo.png",
            },
        },
        // transcript boosts SEO massively when present
        ...(video.transcript ? { transcript: video.transcript.slice(0, 5000) } : {}),
    };

    const SourceIcon = video.source === "tiktok" ? Music2 : Youtube;

    return (
        <div className="min-h-screen" data-testid="transmission-detail-page">
            <Seo
                title={`${video.title} · Foundation of Light`}
                description={cleanDescription || "Transmission de langage de lumière — Foundation of Light."}
                path={`/transmissions/${video.slug}`}
                image={video.thumbnail_url}
                jsonLd={videoSchema}
            />

            <div className="max-w-4xl mx-auto px-6 pt-16 pb-24">
                <Link
                    to="/transmissions"
                    className="inline-flex items-center gap-2 text-sm text-primary mb-6 hover:underline"
                    data-testid="back-to-transmissions"
                >
                    <ArrowLeft className="w-4 h-4" /> Toutes les transmissions
                </Link>

                <div className="text-xs tracking-[0.25em] uppercase text-primary mb-3 font-medium flex items-center gap-2">
                    <SourceIcon className="w-3.5 h-3.5" /> Transmission
                    <span className="text-muted-foreground normal-case tracking-normal flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {formatDate(video.published_at)}
                    </span>
                </div>

                <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mb-8" data-testid="video-title">
                    {video.title}
                </h1>

                {/* Video embed */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-8"
                >
                    <VideoEmbed video={video} />
                </motion.div>

                <div className="flex flex-wrap gap-3 mb-10">
                    <a
                        href={video.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                        data-testid="watch-on-source"
                    >
                        <ExternalLink className="w-4 h-4" />
                        Voir sur {video.source === "tiktok" ? "TikTok" : "YouTube"}
                    </a>
                </div>

                {/* Description */}
                {cleanDescription && (
                    <section className="prose prose-lg max-w-none text-foreground/85 mb-14" data-testid="video-description">
                        <h2 className="font-serif text-2xl mb-4 text-primary">À propos de cette transmission</h2>
                        <div className="whitespace-pre-wrap leading-relaxed">
                            {cleanDescription}
                        </div>
                    </section>
                )}

                {/* Transcript */}
                {video.transcript && (
                    <section className="rounded-3xl bg-card border border-border/60 p-8 md:p-10 mb-14" data-testid="video-transcript">
                        <details open>
                            <summary className="cursor-pointer font-serif text-2xl mb-6 text-primary list-none flex items-center gap-3">
                                <span className="text-primary/50">·</span> Transcription complète
                                {video.transcript_language && (
                                    <span className="text-xs uppercase tracking-widest text-muted-foreground font-sans font-normal">
                                        ({video.transcript_language})
                                    </span>
                                )}
                            </summary>
                            <div className="whitespace-pre-wrap leading-relaxed text-foreground/85 text-base">
                                {video.transcript}
                            </div>
                        </details>
                    </section>
                )}

                {/* Related videos */}
                {related.length > 0 && (
                    <section className="mt-16">
                        <h2 className="font-serif text-2xl mb-6">Autres transmissions</h2>
                        <div className="grid gap-4 sm:grid-cols-3">
                            {related.map((v) => (
                                <Link
                                    key={v.id}
                                    to={`/transmissions/${v.slug}`}
                                    className="group block"
                                    data-testid={`related-${v.id}`}
                                >
                                    <div className="aspect-video rounded-xl overflow-hidden bg-black mb-2">
                                        {v.thumbnail_url && (
                                            <img src={v.thumbnail_url} alt={v.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        )}
                                    </div>
                                    <p className="text-sm line-clamp-2 group-hover:text-primary transition-colors">
                                        {v.title}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
